import { TFunction } from 'i18next';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { publish, SubscriptionTopic } from '../../../../../../viewer/messaging/pubsub';
import { ViewSettings } from '../../../../../../viewer/view-settings';
import { projectStateAdapter } from '../../../../../state/project-state/project-state-adapter';
import { ProgressController } from '../../../../../utils/progress-controller';
import { Button } from '../../../../button/button';
import { CheckBox } from '../../../../check-box/check-box';
import { InfoBox } from '../../../../info-box/info-box';
import { LabelledContainer } from '../../../../labelled-container/labelled-container';
import { OverlayLoader } from '../../../../overlay-loader/overlay-loader';
import { ProgressBar } from '../../../../progress-bar/progress-bar';
import { CollapsableSection } from '../../../../skyview/right-menu/components/collapsable-section';
import { CollapsableSectionGroup } from '../../../../skyview/right-menu/components/collapsable-section-group';
import {
  RightPanelContent,
  RightPanelFooter,
} from '../../../../skyview/right-menu/components/styles';
import { Stack } from '../../../../stack/stack';
import { TextBox } from '../../../../text-box/text-box';
import { reportHelpers } from '../helpers';
import { NoVolumesPanel } from '../helpers/no-volumes-panel';
import { useVolumesWithAssets } from '../helpers/use-volume-with-assets';
import {
  findSelectedVolumes,
  hasSelectedAssets,
  hasValidReportName,
  VolumeWithAsset,
} from '../helpers/utils';
import { VolumeMultiSelectListSection } from '../helpers/volume-multi-select-list-section';

export const VolumeOverviewPdfReportMenu = () => {
  const { t } = useTranslation();
  const [includeOverview, setIncludeOverview] = useState(true);
  const { volumesWithAssets, setVolumesWithAssets } = useVolumesWithAssets();
  const [progress, setProgress] = useState(0);
  const [loading, setLoading] = useState(false);
  const [reportName, setReportName] = useState(t('title', { ns: 'skyviewReport' }) as string);

  const onClickExportReportButton = async () => {
    try {
      setLoading(true);

      const selectedVolumes = findSelectedVolumes(volumesWithAssets);
      const maxSteps = includeOverview ? selectedVolumes.length + 3 : 3;
      const progressController = new ProgressController(
        (progress) => setProgress(progress),
        maxSteps,
      );
      progressController.reset();

      await exportReport(selectedVolumes, reportName, includeOverview, progressController, t);
    } finally {
      // Error is handled with a toaster currently.
      setLoading(false);
    }
  };

  if (volumesWithAssets.length === 0) {
    return <NoVolumesPanel />;
  }

  return (
    <>
      <RightPanelContent useTopPadding={false}>
        <OverlayLoader backgroundColor="#FFFA" showIcon={false} visible={loading}>
          <CollapsableSectionGroup>
            <SettingsSection
              includeOverview={includeOverview}
              reportName={reportName}
              setIncludeOverview={setIncludeOverview}
              setReportName={setReportName}
            />

            <VolumeMultiSelectListSection
              loading={loading}
              setVolumesWithAssets={setVolumesWithAssets}
              volumesWithAssets={volumesWithAssets}
            />
          </CollapsableSectionGroup>
        </OverlayLoader>
      </RightPanelContent>

      <RightPanelFooter>
        <Stack direction="column" spacing={0.4}>
          <InfoBox color="yellow">
            {`${t('export.volumesSelected', { ns: 'skyviewReport' }) as string}: ${
              volumesWithAssets?.filter((x) => x.selected).length ?? 0
            } / ${volumesWithAssets?.length ?? 0}`}
          </InfoBox>

          {loading && <ProgressBar max={1} min={0} value={progress} />}

          <Button
            color={'primary'}
            disabled={
              loading || !hasValidReportName(reportName) || !hasSelectedAssets(volumesWithAssets)
            }
            loading={loading}
            variant={'contained'}
            width="100%"
            onClick={onClickExportReportButton}
          >
            {t('exportButton', { ns: 'skyviewReport' })}
          </Button>
        </Stack>
      </RightPanelFooter>
    </>
  );
};

const SettingsSection = (props: {
  reportName: string;
  setReportName: (value: string) => void;
  includeOverview: boolean;
  setIncludeOverview: (value: boolean) => void;
}) => {
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState(true);
  return (
    <CollapsableSection
      expanded={expanded}
      title={t('settings', { ns: 'skyviewReport' })}
      titleUpperCase={true}
      onSetExpanded={setExpanded}
    >
      <Stack spacing={1}>
        <LabelledContainer text={t('reportTitle', { ns: 'skyviewReport' })}>
          {(formElementId) => (
            <TextBox
              id={formElementId()}
              value={props.reportName}
              onChange={(e) => props.setReportName(e.target.value)}
            />
          )}
        </LabelledContainer>

        {!ViewSettings.instance.isCadViewer && (
          <CheckBox
            checked={props.includeOverview}
            onChange={() => props.setIncludeOverview(!props.includeOverview)}
          >
            {t('settingsPrism.includeOverview', { ns: 'skyviewReport' })}
          </CheckBox>
        )}
      </Stack>
    </CollapsableSection>
  );
};

async function exportReport(
  selectedVolumes: VolumeWithAsset[],
  reportName: string,
  includeOverview: boolean,
  progressController: ProgressController,
  t: TFunction,
) {
  try {
    await reportHelpers.exportVolumePdfReport(
      projectStateAdapter.getSnapshot().project!.name,
      reportName,
      selectedVolumes,
      includeOverview,
      progressController,
    );
  } catch (error) {
    publish(SubscriptionTopic.ToastrMessage, {
      message: t('exportError', { ns: 'skyviewReport' }) as string,
      type: 'error',
    });
  }
}
